import mushodoPromise from '@webapp-next/lib/request/webapp/mushodo-promise';
import mushodoCheckStatusBackgroundJob from '@webapp-next/lib/request/webapp/mushodo-check-status-background-job';

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Get the number of minutes difference between the user's timezone
 * and the timezone set for the location
 * @param {Object} vars
 * @param {String[]} vars.payrollIds
 * @param {Number[]} vars.locationIds
 * @return {Object}
 */
export default async function getTeamPayrollConfig({
  payrollIds,
  locationIds,
}) {
  const reply = await mushodoPromise('GetTeamPayrollConfig', {
    strServiceArray: payrollIds,
    intCompanyIdArray: locationIds,
  });

  // Explicit backend marker: no requested payroll service has stored
  // connection tokens, so no background job was dispatched.
  if (reply?.blnNoConnectedPayroll) {
    return { success: true, message: '', data: {} };
  }

  // Channel-level failures arrive as an HTTP 200 payload with an error key.
  if (reply?.error) {
    throw new Error(reply.error);
  }

  const { strSyncKey } = reply || {};
  if (!strSyncKey) {
    throw new Error('Unexpected GetTeamPayrollConfig reply: no sync key');
  }

  // Bound the poll: without a deadline a reply that keeps returning a sync key
  // would poll forever. Awaiting each status check in turn keeps at most one
  // request in flight, and a rejected check rejects this function's Promise
  // rather than leaving the caller's loading state stuck.
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const status = await mushodoCheckStatusBackgroundJob(strSyncKey);

    if (status.success && !status.result?.strSyncKey) {
      return { success: true, message: '', data: status.result || {} };
    }
    if (status.result?.error) {
      throw new Error(status.result?.error || 'Unable to get payroll config');
    }
    await sleep(POLL_INTERVAL_MS);
  }

  throw new Error('GetTeamPayrollConfig polling timed out');
}
